import React, { useContext } from 'react'
import { InputWrapper } from '../../components'
import { WemoloCheckbox, WemoloInput, WemoloTypography } from '../../components/Wemolo'
import { SectionBlock } from './components/SectionBlock'
import { LabeledToggleRow } from './components/LabelToggleRow'
import { DeviceTypeEnum } from '../../data/enums'
import { LotContext } from '../Lot/LotContext'
import { OccupancyConfigState } from './hooks/useOccupancyConfig'
import { OccupancySettingsErrors } from './OccupancyView'
import styles from './OccupancySettings.module.css'

interface OccupancySettingsProps {
    errors: OccupancySettingsErrors
    handleInputChange: (key: keyof OccupancyConfigState, value: string | number | undefined) => void
}

export const OccupancySettings = ({ errors, handleInputChange }: OccupancySettingsProps) => {
    const lotContext = useContext(LotContext)
    const lotCapacity = lotContext.selectedLot?.size ? lotContext.selectedLot?.size : 0
    const stations = lotContext.lotStations?.station || []
    const checkBoxStations = stations
        .filter((station) => station.type === DeviceTypeEnum.SCANNER)
        .map((item) => {
            const id = `${item.station_name}`
            return {
                id,
                label: `${id} (${item.device_direction})`
            }
        })
    const selectedStations = checkBoxStations
        .map((s) => s.id)
        .filter((id) => !lotContext.occupancyConfig?.excluded_stations.includes(id))

    return (
        <>
            <SectionBlock label="Capacity Settings" caption="Configure total and reserved parking capacity">
                <div className={styles.row}>
                    <div className={styles.cell}>
                        <InputWrapper
                            label="Total Parking Lot Capacity"
                            caption="Set in Salesforce, contact CS to update"
                        >
                            <WemoloInput value={lotCapacity} disabled type="number" />
                        </InputWrapper>
                    </div>
                    <div className={styles.cell}>
                        <InputWrapper
                            label="Reserved for vehicles with parking permit"
                            caption="Number of spaces reserved for vehicles with a parking permit in the occupancy count"
                        >
                            <WemoloInput
                                value={lotContext.occupancyConfig?.lot_capacity_wl}
                                onChange={(e) => handleInputChange('lot_capacity_wl', e.target.value)}
                                error={errors.reservedOverCapacity}
                                type="number"
                            />
                        </InputWrapper>
                    </div>
                </div>
            </SectionBlock>

            <SectionBlock
                label="Select stations for occupancy calculation"
                caption="Select which stations will be used to calculate the occupancy. At least one station must be selected."
            >
                <div className={styles.stationGrid}>
                    {checkBoxStations.map((station) => (
                        <WemoloCheckbox
                            key={station.id}
                            label={station.label}
                            checked={selectedStations.includes(station.id)}
                            onChange={() => {
                                lotContext.setOccupancyConfig((prev) => {
                                    if (!prev) return prev
                                    const isExcluded = prev.excluded_stations.includes(station.id)
                                    const updatedExcluded = isExcluded
                                        ? prev.excluded_stations.filter((id) => id !== station.id)
                                        : [...prev.excluded_stations, station.id]
                                    return { ...prev, excluded_stations: updatedExcluded }
                                })
                            }}
                        />
                    ))}
                </div>
            </SectionBlock>

            <SectionBlock
                label="Timeout Settings"
                caption="Set the timeout period after which vehicles are considered to have left"
            >
                <div className={styles.row}>
                    <div className={styles.cell}>
                        <InputWrapper
                            label="Parking Permit Vehicle Timeout (hours)"
                            caption="Time before parking permit vehicles are considered to have left (recommended 24h)"
                        >
                            <WemoloInput
                                value={
                                    lotContext.occupancyConfig?.timeout_wl != null
                                        ? lotContext.occupancyConfig.timeout_wl / 3600
                                        : ''
                                }
                                type="number"
                                onChange={(e) => {
                                    const hours = e.target.value
                                    const newValue = hours === '' ? undefined : Number(hours) * 3600
                                    handleInputChange('timeout_wl', newValue)
                                }}
                                error={errors.parkingPermitTimeoutInvalid}
                            />
                        </InputWrapper>
                    </div>
                    <div className={styles.cell}>
                        <InputWrapper
                            label="Standard Vehicle Timeout (hours)"
                            caption="Time before standard vehicles are considered to have left (recommended 8h)"
                        >
                            <WemoloInput
                                value={
                                    lotContext.occupancyConfig?.timeout != null
                                        ? lotContext.occupancyConfig.timeout / 3600
                                        : ''
                                }
                                type="number"
                                onChange={(e) => {
                                    const hours = e.target.value
                                    const seconds = hours === '' ? undefined : Number(hours) * 3600
                                    handleInputChange('timeout', seconds)
                                }}
                                error={errors.standardTimeoutInvalid}
                            />
                        </InputWrapper>
                    </div>
                </div>
            </SectionBlock>

            <SectionBlock label="License Plate Detection Settings">
                <WemoloTypography tag="div" variant="label-small">
                    Only license plates with a confidence score equal to or above this value will be considered in
                    occupancy calculation.
                </WemoloTypography>
                <ul className={styles.helperList}>
                    <li>
                        <WemoloTypography variant="label-small" tag="span">
                            - Set to 0 to accept all detections (no filtering).
                        </WemoloTypography>
                    </li>
                    <li>
                        <WemoloTypography variant="label-small" tag="span">
                            - Set to 0.95 (or 0,95) to only accept plates where the model is 95% sure or more.
                        </WemoloTypography>
                    </li>
                    <li>
                        <WemoloTypography variant="label-small" tag="span">
                            - Both comma and dot are accepted as decimal separators.
                        </WemoloTypography>
                    </li>
                </ul>
                <div className={styles.row}>
                    <div className={styles.cell}>
                        <InputWrapper
                            label="LPness Threshold"
                            text="This setting filters license plate detections based on model confidence. Set a higher value to only accept highly confident detections. Experimental feature."
                        >
                            <WemoloInput
                                value={lotContext.occupancyConfig?.lpness}
                                onChange={(e) => handleInputChange('lpness', e.target.value)}
                                error={errors.lpThresholdOutOfRange}
                                type="number"
                            />
                        </InputWrapper>
                    </div>
                    <div className={styles.cell}>
                        <InputWrapper
                            label="Licence Plate Recognition (LPR) Confidence"
                            text="This setting filters license plate detections based on model confidence. Set a higher value to only accept highly confident detections."
                        >
                            <WemoloInput
                                value={lotContext.occupancyConfig?.lpr}
                                onChange={(e) => handleInputChange('lpr', e.target.value)}
                                error={errors.lprOutofRange}
                                type="number"
                            />
                        </InputWrapper>
                    </div>
                </div>
            </SectionBlock>

            <SectionBlock
                label="Vehicle Type Exclusions"
                caption="Select vehicle types to exclude from occupancy calculations"
                last={true}
            >
                <div className={styles.exclusionsBlock}>
                    <WemoloTypography variant="body-medium" tag="div" className={styles.docsLine}>
                        For vehicle type definitions, please refer to the documentation here:{' '}
                        <a
                            href="https://www.notion.so/Supported-vehicles-type-recognition-1e78cdb99142811a802dfdd9ff9077af?source=copy_link"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.docsLink}
                        >
                            Notion Page
                        </a>
                    </WemoloTypography>
                    <LabeledToggleRow
                        label="Exclude Trucks"
                        caption="Exclude trucks from occupancy calculation"
                        checked={lotContext.occupancyConfig?.truck || false}
                        onToggle={(val) =>
                            lotContext.setOccupancyConfig((prev) => (prev ? { ...prev, truck: val } : prev))
                        }
                        icon={<i className="material-icons">local_shipping</i>}
                    />
                    <LabeledToggleRow
                        label="Exclude Motorcycles"
                        caption="Exclude motorcycles from occupancy calculation"
                        checked={lotContext.occupancyConfig?.motorcycle || false}
                        onToggle={(val) =>
                            lotContext.setOccupancyConfig((prev) => (prev ? { ...prev, motorcycle: val } : prev))
                        }
                        icon={<i className="material-icons">motorcycle</i>}
                    />
                    <LabeledToggleRow
                        label="Exclude vehicles with unknown vehicle type"
                        caption="Exclude vehicles with unknown vehicle type from occupancy calculation"
                        checked={lotContext.occupancyConfig?.undetected_vehicles || false}
                        onToggle={(val) =>
                            lotContext.setOccupancyConfig((prev) =>
                                prev ? { ...prev, undetected_vehicles: val } : prev
                            )
                        }
                        icon={<i className="material-icons">motorcycle</i>}
                    />
                </div>
            </SectionBlock>
        </>
    )
}
